import { useCallback, useEffect, useState } from "react";
import { Download, Printer } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Chip, Empty, Kpi, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { downloadPdf, printHtml } from "@/lib/printDoc";
import { invoiceHtml } from "@/components/property/BookingFinance";
import { money, shortDate } from "@/lib/format";

const OK = "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
const WARN = "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300";
const BAD = "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300";

export const Invoices = ({ onOpenBooking }) => {
  const [data, setData] = useState(null);
  const [due, setDue] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [invoices, money] = await Promise.all([
      api.get("/property/invoices"),
      api.get("/property/money-due"),
    ]);
    setData(invoices.data);
    setDue(money.data);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const open = async (row, mode) => {
    try {
      if (mode === "pdf") {
        await downloadPdf(`/property/invoices/${row.invoice_ref}/pdf`, {},
          `hire-invoice-${row.invoice_ref}.pdf`);
      } else {
        const res = await api.get(`/property/invoices/${row.invoice_ref}`);
        printHtml(`Hire invoice ${row.invoice_ref}`, invoiceHtml(res.data));
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return null;
  const today = new Date().toISOString().slice(0, 10);

  const chase = async (row) => {
    setBusy(`${row.booking_ref}-${row.kind}`);
    try {
      const res = await api.post(`/property/bookings/${row.booking_ref}/chase`,
        { kind: row.kind });
      const mail = res.data.email;
      window.open(`mailto:${encodeURIComponent(mail.to || "")}`
        + `?subject=${encodeURIComponent(mail.subject)}`
        + `&body=${encodeURIComponent(mail.body)}`, "_blank");
      toast.success(`Chaser drafted for ${row.hirer} — recorded against ${row.booking_ref}`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  return (
    <div className="space-y-4" data-testid="hire-invoices">
      {due && (
        <Panel testId="panel-money-due" kicker="Chasing"
               title="Money due on hall hire"
               actions={(
                 <div className="flex items-center gap-2">
                   <span className="text-[10.5px] text-muted-foreground" data-testid="reminder-schedule">
                     reminders {due.schedule.days_before.join(" and ")} days before
                     {due.schedule.chase_overdue
                       ? `, then every ${due.schedule.overdue_every_days} days once overdue` : ""}
                   </span>
                   <Button size="sm" variant="outline" data-testid="run-reminders"
                           disabled={busy === "run"}
                           onClick={async () => {
                             setBusy("run");
                             try {
                               const res = await api.post("/property/reminders/run");
                               toast.success(`${res.data.due_soon.length} due soon and `
                                 + `${res.data.overdue.length} overdue chased`);
                               await load();
                             } catch (e) { toast.error(apiError(e)); }
                             setBusy("");
                           }}
                           className="h-7 rounded-full border-border px-2.5 text-[10px]">
                     Run reminders now
                   </Button>
                 </div>
               )}>
          <div className="grid grid-cols-2 gap-3 px-3 py-3 lg:grid-cols-4">
            <Kpi testId="kpi-due-soon" label="Due soon" value={due.counters.due_soon} accent="amber" />
            <Kpi testId="kpi-overdue" label="Overdue" value={due.counters.overdue} accent="rose"
                 sub={money(due.counters.overdue_amount, "INR", true)} />
            <Kpi testId="kpi-due-amount" label="Still to collect"
                 value={money(due.counters.due_amount, "INR", true)} accent="violet" />
            <Kpi testId="kpi-due-lines" label="Payments tracked" value={due.items.length} />
          </div>
          <Table testId="money-due-table"
                 head={<><Th>Booking</Th><Th>Hirer</Th><Th>What</Th><Th>Amount</Th><Th>Due</Th>
                   <Th>Last chased</Th><Th /></>}>
            {due.items.length === 0 && <Empty colSpan={7} label="Nothing outstanding — every hire is paid up" />}
            {due.items.map((r) => (
              <tr key={`${r.booking_ref}-${r.kind}`} className="row-hover"
                  data-testid={`due-row-${r.booking_ref}-${r.kind}`}>
                <Td className="font-mono text-[10.5px]">{r.booking_ref}
                  <div className="text-[10px] text-muted-foreground">{r.space_name}</div></Td>
                <Td className="text-[11.5px] text-foreground">{r.hirer}</Td>
                <Td className="text-[11px]">{r.kind}</Td>
                <Td className="font-mono text-[11px]">{money(r.amount, r.currency)}</Td>
                <Td><Chip testId={`due-state-${r.booking_ref}-${r.kind}`}
                          className={r.overdue ? BAD : WARN}>
                  {r.overdue ? `${Math.abs(r.days_to_due)}d overdue` : `in ${r.days_to_due}d`}
                </Chip>
                  <div className="font-mono text-[10px] text-muted-foreground">{shortDate(r.due_on)}</div></Td>
                <Td className="font-mono text-[10px]">
                  {r.last_chased_on ? shortDate(r.last_chased_on) : "never"}</Td>
                <Td>
                  <Button size="sm" variant="outline"
                          data-testid={`chase-${r.booking_ref}-${r.kind}`}
                          disabled={busy === `${r.booking_ref}-${r.kind}`}
                          onClick={() => chase(r)}
                          className="h-7 rounded-full border-border px-2.5 text-[10px]">
                    Chase now</Button>
                </Td>
              </tr>
            ))}
          </Table>
        </Panel>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testId="kpi-invoices" label="Hire invoices" value={data.counters.invoices} />
        <Kpi testId="kpi-invoiced" label="Invoiced" accent="violet"
             value={money(data.counters.invoiced, "INR", true)} />
        <Kpi testId="kpi-collected" label="Collected" accent="emerald"
             value={money(data.counters.collected, "INR", true)} />
        <Kpi testId="kpi-outstanding" label="Outstanding" accent="amber"
             value={money(data.counters.outstanding, "INR", true)}
             sub={`${data.counters.overdue} past due`} />
      </div>
      <Panel testId="panel-invoices" kicker="Hall hire" title="Invoices raised">
        <Table testId="invoices-table"
               head={<><Th>Invoice</Th><Th>Hirer</Th><Th>Space</Th><Th>Event</Th><Th>Due</Th>
                 <Th>Amount</Th><Th>Paid</Th><Th>Outstanding</Th><Th>Status</Th><Th /></>}>
          {data.items.length === 0 && <Empty colSpan={10} label="No hire invoices raised yet" />}
          {data.items.map((r) => (
            <tr key={r.invoice_ref} className="row-hover" data-testid={`invoice-row-${r.invoice_ref}`}>
              <Td className="font-mono text-[11px]">{r.invoice_ref}
                <div className="text-[10px] text-muted-foreground">{r.booking_ref}</div></Td>
              <Td className="text-[11.5px] text-foreground">{r.hirer || "—"}</Td>
              <Td className="text-[11.5px]">{r.space_name}</Td>
              <Td className="font-mono text-[10.5px]">{shortDate(r.event_date)}</Td>
              <Td className="font-mono text-[10.5px]">{r.due_on ? shortDate(r.due_on) : "—"}</Td>
              <Td className="font-mono text-[11px]">{money(r.amount, r.currency)}</Td>
              <Td className="font-mono text-[11px]">{money(r.paid, r.currency)}</Td>
              <Td className="font-mono text-[11px]">{money(r.outstanding, r.currency)}</Td>
              <Td><Chip testId={`invoice-status-${r.invoice_ref}`}
                        className={r.status === "paid" ? OK
                          : r.outstanding > 0 && (r.due_on || "9999") < today ? BAD : WARN}>
                {r.status}</Chip></Td>
              <Td className="space-x-1.5 whitespace-nowrap">
                <Button size="sm" variant="outline" data-testid={`invoice-print-${r.invoice_ref}`}
                        onClick={() => open(r, "print")}
                        className="h-7 rounded-full border-border px-2 text-[10px]">
                  <Printer className="h-3 w-3" /></Button>
                <Button size="sm" variant="outline" data-testid={`invoice-pdf-${r.invoice_ref}`}
                        onClick={() => open(r, "pdf")}
                        className="h-7 rounded-full border-border px-2 text-[10px]">
                  <Download className="h-3 w-3" /></Button>
                <Button size="sm" variant="outline" data-testid={`invoice-money-${r.invoice_ref}`}
                        onClick={() => onOpenBooking?.(r.booking_ref)}
                        className="h-7 rounded-full border-border px-2.5 text-[10px]">Payments</Button>
              </Td>
            </tr>
          ))}
        </Table>
      </Panel>
    </div>
  );
};
